/**
 * Waypoint editor, for a waypoint that's part of a route or a standalone mark.
 * Rename it, type its position in degrees, minutes and decimal minutes, pick its
 * marker icon, or delete it. Standalone marks also get a "Go to" that starts a
 * direct goto (the active-leg line and live data, the same machinery as Follow
 * route, but for one point). Dragging the marker on the chart moves it too.
 *
 * In Live we're following, not building, so the GEOMETRY is protected: no
 * typed position, no accidental re-route. The safe edits stay available
 * (fb-msmbcda5-d): name, marker, notes, and the arrival alarm, none of which
 * can move the route under a boat that is following it. Position editing
 * stays a Planning job.
 */
import { useEffect, useState } from 'react';
import { useChartPlotterStore } from '@/store';
import { useShellStore, type WaypointRef } from '@/store/shell';
import { useRoutingStore } from '@/store/routing';
import type { Route, Waypoint } from '@/routing';
import {
  formatLatLon,
  formatPosition,
  formatBearing,
  formatDistance,
  parsePosition,
  rhumbBearingDeg,
  rhumbDistanceNm,
} from '@/nav';
import { Sheet } from './Sheet';
import { haptic } from '@/ui/haptics';

const ICONS: { id: string; glyph: string; name: string }[] = [
  { id: 'plot-fix', glyph: '●', name: 'Circle' },
  { id: 'plot-waypoint', glyph: '■', name: 'Square' },
  { id: 'plot-ep', glyph: '▲', name: 'Triangle' },
  { id: 'plot-star', glyph: '★', name: 'Star' },
  { id: 'plot-anchor', glyph: '⚓', name: 'Anchor' },
  { id: 'plot-hazard', glyph: '⚠', name: 'Hazard' },
  { id: 'plot-fish', glyph: '🐟', name: 'Fish' },
  { id: 'plot-buoy', glyph: '◈', name: 'Buoy' },
];

function refKey(ref: WaypointRef | null): string {
  if (!ref) return '';
  return ref.type === 'route' ? `r:${ref.routeId}:${ref.wpId}` : `m:${ref.markId}`;
}

/** A plain key/value readout row, the same style the feature info sheet uses. */
function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="acp-kv">
      <span className="k">{k}</span>
      <span className="v">{v}</span>
    </div>
  );
}

/** Bearing then distance between two points, e.g. "045°T  1.23 nm". */
function legText(from: [number, number], to: [number, number]): string {
  return `${formatBearing(rhumbBearingDeg(from, to))}  ${formatDistance(rhumbDistanceNm(from, to))}`;
}

export function WaypointSheet() {
  const editing = useShellStore((s) => s.editingWaypoint);
  const setEditingWaypoint = useShellStore((s) => s.setEditingWaypoint);
  const closeSheet = useShellStore((s) => s.closeSheet);
  const showToast = useShellStore((s) => s.showToast);
  const mode = useShellStore((s) => s.mode);
  const dataFields = useShellStore((s) => s.dataFields);
  const setDataFieldAt = useShellStore((s) => s.setDataFieldAt);
  const setMode = useShellStore((s) => s.setMode);

  const routes = useRoutingStore((s) => s.routes);
  const marks = useRoutingStore((s) => s.marks);
  const updateWaypoint = useRoutingStore((s) => s.updateWaypoint);
  const removeWaypoint = useRoutingStore((s) => s.removeWaypoint);
  const updateMark = useRoutingStore((s) => s.updateMark);
  const removeMark = useRoutingStore((s) => s.removeMark);
  const gotoMark = useRoutingStore((s) => s.gotoMark);
  const activateRouteFrom = useRoutingStore((s) => s.activateRouteFrom);
  const boat = useChartPlotterStore((s) => s.ownship.position);

  const isMark = editing?.type === 'mark';
  let wp: Waypoint | undefined;
  let route: Route | undefined;
  let wpIndex = -1;
  if (editing?.type === 'route') {
    route = routes.find((r) => r.id === editing.routeId);
    wpIndex = route?.waypoints.findIndex((w) => w.id === editing.wpId) ?? -1;
    wp = wpIndex >= 0 ? route!.waypoints[wpIndex] : undefined;
  } else if (editing?.type === 'mark') {
    wp = marks.find((m) => m.id === editing.markId);
  }

  const [name, setName] = useState('');
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [icon, setIcon] = useState<string>('plot-waypoint');
  const [description, setDescription] = useState('');
  const [alarmOn, setAlarmOn] = useState(false);
  const [radius, setRadius] = useState('0.10');

  const key = refKey(editing);
  const lonValue = wp?.position[0];
  const latValue = wp?.position[1];
  const radiusValue = wp?.arrivalRadiusNm;
  useEffect(() => {
    if (!wp) return;
    setName(wp.name ?? '');
    setLat(formatLatLon(wp.position[1], 'lat'));
    setLon(formatLatLon(wp.position[0], 'lon'));
    setIcon(wp.icon ?? 'plot-waypoint');
    setDescription(wp.description ?? '');
    setAlarmOn(typeof wp.arrivalRadiusNm === 'number');
    // Round so a live ring drag shows a tidy value in the field.
    setRadius(wp.arrivalRadiusNm != null ? String(Number(wp.arrivalRadiusNm.toFixed(3))) : '0.10');
    // Re-seed on identity, position and radius (radius so a ring drag stays in
    // sync); typing isn't clobbered because that only changes local state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, lonValue, latValue, radiusValue]);

  if (!editing || !wp) {
    return <Sheet name="waypoint" title="Waypoint" />;
  }

  const ref = editing;

  // ---- Live: the route's GEOMETRY is protected (no typed position), but the
  // safe edits stay: name, marker, notes, arrival alarm. None of those can move
  // the route under a boat that is following it, and being told to go and find
  // Planning to rename a waypoint mid-passage was the wrong kind of protection
  // (fb-msmbcda5-d). Standalone marks stay fully editable in every mode.
  if (mode === 'Live' && !isMark) {
    const here = wp.position;
    const wps = route?.waypoints ?? [];
    return (
      <Sheet
        name="waypoint"
        title="Route waypoint"
        sub="Name, marker, notes and the arrival alarm can change here. Moving the waypoint is a Planning job, so a followed route never shifts under us."
      >
        <Row k="Position" v={formatPosition(here)} />
        <Row k="From boat" v={boat ? legText(boat, here) : 'No fix yet'} />
        {ref.type === 'route' && route && wpIndex >= 0 && (
          <>
            <Row k="Route" v={route.name || 'Route'} />
            <Row k="Waypoint" v={`${wpIndex + 1} of ${wps.length}`} />
            {wpIndex > 0 && <Row k="Leg in" v={legText(wps[wpIndex - 1].position, here)} />}
            {wpIndex < wps.length - 1 && (
              <Row k="Leg out" v={legText(here, wps[wpIndex + 1].position)} />
            )}
          </>
        )}
        <label className="acp-fieldlabel">Name</label>
        <input className="acp-text-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="WP1" />
        <label className="acp-fieldlabel">Marker</label>
        <div className="acp-iconrow">
          {ICONS.map((ic) => (
            <button
              key={ic.id}
              type="button"
              className={`acp-iconbtn${icon === ic.id ? ' on' : ''}`}
              onClick={() => setIcon(ic.id)}
            >
              {ic.glyph}
              <span className="nm">{ic.name}</span>
            </button>
          ))}
        </div>
        <label className="acp-fieldlabel">Notes</label>
        <textarea
          className="acp-note-input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          placeholder="Description or info — e.g. pot buoys here at low water"
        />
        <label className="acp-fieldlabel">Arrival alarm</label>
        <div className="acp-seg">
          <button type="button" className={!alarmOn ? 'on' : ''} onClick={() => setAlarmOn(false)}>
            Off
          </button>
          <button type="button" className={alarmOn ? 'on' : ''} onClick={() => setAlarmOn(true)}>
            On
          </button>
        </div>
        {alarmOn && (
          <input
            className="acp-text-input"
            value={radius}
            onChange={(e) => setRadius(e.target.value)}
            placeholder="0.10"
            inputMode="decimal"
            aria-label="Alarm radius in nautical miles"
          />
        )}
        <div className="acp-btn-row">
          <button type="button" className="acp-btn primary" onClick={saveSafe}>
            Save
          </button>
        </div>
        {ref.type === 'route' && route && route.waypoints.length >= 2 && (
          <div className="acp-btn-row">
            <button type="button" className="acp-btn primary full" onClick={followFromHere}>
              Follow from this waypoint
            </button>
          </div>
        )}
        {/* Deleting is removal, not editing a route we're following, so we allow
            it even in Live, the same as a clearing bearing: a waypoint dropped
            by mistake can always be cleared away. It re-links the legs around
            it, and removes the route if it was the last one. */}
        <div className="acp-btn-row">
          <button type="button" className="acp-btn full" onClick={del}>
            Delete waypoint
          </button>
        </div>
      </Sheet>
    );
  }

  function commit(patch: Partial<Pick<Waypoint, 'position' | 'name' | 'icon' | 'description' | 'arrivalRadiusNm' | 'arrivalAlarm'>>) {
    if (ref.type === 'route') updateWaypoint(ref.routeId, ref.wpId, patch);
    else updateMark(ref.markId, patch);
  }

  // Save for the Live sheet: everything except position, which is what makes
  // it safe to offer while following.
  function saveSafe() {
    const rad = parseFloat(radius);
    const arrivalRadiusNm = alarmOn && Number.isFinite(rad) && rad > 0 ? rad : undefined;
    haptic('select');
    commit({ name: name.trim() || wp!.name, icon, description: description.trim() || undefined, arrivalRadiusNm, arrivalAlarm: alarmOn });
    setEditingWaypoint(null);
    closeSheet();
    showToast('Waypoint updated');
  }

  function save() {
    const parsed = parsePosition(`${lat} ${lon}`);
    if (!parsed) {
      showToast('Could not read that position');
      return;
    }
    const rad = parseFloat(radius);
    const arrivalRadiusNm = alarmOn && Number.isFinite(rad) && rad > 0 ? rad : undefined;
    haptic('select');
    commit({ name: name.trim() || wp!.name, position: parsed, icon, description: description.trim() || undefined, arrivalRadiusNm, arrivalAlarm: alarmOn });
    setEditingWaypoint(null);
    closeSheet();
    showToast('Waypoint updated');
  }

  function del() {
    // Losing a waypoint or mark is destructive, so it gets the firm commit buzz.
    haptic('commit');
    if (ref.type === 'route') removeWaypoint(ref.routeId, ref.wpId);
    else removeMark(ref.markId);
    setEditingWaypoint(null);
    closeSheet();
    showToast('Waypoint deleted');
  }

  function goTo() {
    if (ref.type !== 'mark' || !boat) {
      showToast('No fix yet');
      return;
    }
    haptic('commit');
    gotoMark(ref.markId, boat);
    const routeFields = ['dtwp', 'brgwp', 'xte', 'eta'];
    if (!dataFields.some((f) => routeFields.includes(f))) setDataFieldAt(0, 'dtwp');
    setEditingWaypoint(null);
    closeSheet();
    showToast('Going to waypoint');
  }

  // Follow the route starting at this waypoint's leg, rather than from the
  // route start. Hands to Live, the same way "Follow in Live" does.
  function followFromHere() {
    if (ref.type !== 'route' || !route) return;
    const legCount = route.waypoints.length - 1;
    if (legCount < 1) {
      showToast('Add another waypoint to follow');
      return;
    }
    // Follow from WP3 means MAKE FOR WP3: the leg that ends at this waypoint,
    // which is leg (wpIndex - 1). Using wpIndex targeted the NEXT waypoint,
    // because leg N starts at waypoint N (fb-msndewao-2: "it immediately
    // jumped to the next waypoint"). WP1 has no leg ending at it, so index 0
    // clamps to the first leg, whose start the approach logic already covers.
    const legIndex = Math.max(0, Math.min(wpIndex - 1, legCount - 1));
    setMode('Live');
    haptic('commit');
    activateRouteFrom(route.id, legIndex);
    const routeFields = ['dtwp', 'brgwp', 'xte', 'eta'];
    if (!dataFields.some((f) => routeFields.includes(f))) setDataFieldAt(0, 'dtwp');
    setEditingWaypoint(null);
    closeSheet();
    showToast('Following from this waypoint');
  }

  return (
    <Sheet
      name="waypoint"
      title={isMark ? 'Waypoint' : 'Route waypoint'}
      sub="Rename it, type its position in degrees, minutes and decimal minutes, pick a marker, or delete it. You can also drag the marker on the chart."
    >
      <label className="acp-fieldlabel">Name</label>
      <input className="acp-text-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="WP1" />
      <label className="acp-fieldlabel">Latitude</label>
      <input
        className="acp-text-input"
        value={lat}
        onChange={(e) => setLat(e.target.value)}
        placeholder="50 47.832 N"
      />
      <label className="acp-fieldlabel">Longitude</label>
      <input
        className="acp-text-input"
        value={lon}
        onChange={(e) => setLon(e.target.value)}
        placeholder="001 18.456 W"
      />
      <label className="acp-fieldlabel">Marker</label>
      <div className="acp-iconrow">
        {ICONS.map((ic) => (
          <button
            key={ic.id}
            type="button"
            className={`acp-iconbtn${icon === ic.id ? ' on' : ''}`}
            onClick={() => setIcon(ic.id)}
          >
            {ic.glyph}
            <span className="nm">{ic.name}</span>
          </button>
        ))}
      </div>
      <label className="acp-fieldlabel">Notes</label>
      <textarea
        className="acp-note-input"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={2}
        placeholder="Description or info — e.g. pot buoys here at low water"
      />
      <label className="acp-fieldlabel">Proximity alarm</label>
      <div className="acp-seg">
        <button type="button" className={!alarmOn ? 'on' : ''} onClick={() => setAlarmOn(false)}>
          Off
        </button>
        <button type="button" className={alarmOn ? 'on' : ''} onClick={() => setAlarmOn(true)}>
          On
        </button>
      </div>
      {alarmOn && (
        <input
          className="acp-text-input"
          value={radius}
          onChange={(e) => setRadius(e.target.value)}
          placeholder="0.10"
          inputMode="decimal"
          aria-label="Alarm radius in nautical miles"
        />
      )}
      {isMark && (
        <div className="acp-btn-row">
          <button type="button" className="acp-btn primary full" onClick={goTo}>
            Go to this waypoint
          </button>
        </div>
      )}
      {ref.type === 'route' && route && route.waypoints.length >= 2 && (
        <div className="acp-btn-row">
          <button type="button" className="acp-btn full" onClick={followFromHere}>
            Follow from this waypoint
          </button>
        </div>
      )}
      <div className="acp-btn-row">
        <button type="button" className="acp-btn" onClick={del}>
          Delete
        </button>
        <button type="button" className="acp-btn primary" onClick={save}>
          Save
        </button>
      </div>
    </Sheet>
  );
}
